'use client';

// src/componentes.tsx
import {
  useEffect,
  useId,
  useRef
} from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var cx = (...clases) => clases.filter(Boolean).join(" ");
function Icono({ nombre, relleno, tamano, className, style, ...resto }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      "aria-hidden": "true",
      className: cx("ms", relleno && "ms-relleno", className),
      style: tamano ? { fontSize: tamano, ...style } : style,
      ...resto,
      children: nombre
    }
  );
}
var VARIANTES = {
  primario: "bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 disabled:bg-stone-300 dark:disabled:bg-stone-700",
  secundario: "bg-white text-stone-900 border border-stone-300 hover:bg-stone-50 active:bg-stone-100 dark:bg-stone-800 dark:text-stone-100 dark:border-stone-600 dark:hover:bg-stone-700",
  peligro: "bg-white text-rose-700 border border-rose-300 hover:bg-rose-50 dark:bg-transparent dark:text-rose-300 dark:border-rose-800",
  fantasma: "bg-transparent text-primary-700 hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-primary-900/40",
  acento: "bg-amber-400 text-stone-900 hover:bg-amber-300 active:bg-amber-500 shadow-lg shadow-amber-400/30"
};
var TAMANOS = {
  sm: "h-9 px-3 text-sm gap-1.5",
  md: "h-12 px-5 text-[15px] gap-2",
  lg: "h-14 px-6 text-base gap-2"
};
function Boton({
  variante = "primario",
  tamano = "md",
  icono,
  bloque,
  cargando,
  className,
  children,
  disabled,
  type = "button",
  ...resto
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type,
      disabled: disabled || cargando,
      className: cx(
        "inline-flex items-center justify-center rounded-full font-medium transition-colors select-none",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:opacity-60",
        VARIANTES[variante],
        TAMANOS[tamano],
        bloque && "w-full",
        className
      ),
      ...resto,
      children: [
        cargando ? /* @__PURE__ */ jsx(Icono, { nombre: "progress_activity", className: "animate-spin" }) : icono ? /* @__PURE__ */ jsx(Icono, { nombre: icono }) : null,
        children
      ]
    }
  );
}
function Tarjeta({ relleno = "md", resaltada, className, ...resto }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cx(
        "rounded-3xl border bg-white dark:bg-stone-900",
        resaltada ? "border-primary-500 ring-2 ring-primary-200 dark:ring-primary-900" : "border-stone-200 dark:border-stone-800",
        relleno === "md" && "p-4",
        relleno === "sm" && "p-3",
        className
      ),
      ...resto
    }
  );
}
var TONOS = {
  neutro: "bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300",
  exito: "bg-primary-100 text-primary-800 dark:bg-primary-900/60 dark:text-primary-200",
  aviso: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
  peligro: "bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200",
  info: "bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200",
  primario: "bg-primary-600 text-white",
  oscuro: "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900"
};
function Chip({ tono = "neutro", icono, className, children, ...resto }) {
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cx(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium",
        TONOS[tono],
        className
      ),
      ...resto,
      children: [
        icono && /* @__PURE__ */ jsx(Icono, { nombre: icono, tamano: 14 }),
        children
      ]
    }
  );
}
function Campo({ etiqueta, ayuda, error, icono, className, id, ...resto }) {
  const autoId = useId();
  const campoId = id ?? autoId;
  return /* @__PURE__ */ jsxs("div", { className, children: [
    etiqueta && /* @__PURE__ */ jsx("label", { htmlFor: campoId, className: "mb-1.5 block text-xs font-medium text-stone-600 dark:text-stone-400", children: etiqueta }),
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      icono && /* @__PURE__ */ jsx(
        Icono,
        {
          nombre: icono,
          className: "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
        }
      ),
      /* @__PURE__ */ jsx(
        "input",
        {
          id: campoId,
          "aria-invalid": error ? true : void 0,
          "aria-describedby": error || ayuda ? `${campoId}-ayuda` : void 0,
          className: cx(
            "h-12 w-full rounded-2xl border bg-white px-4 text-base text-stone-900 placeholder:text-stone-400",
            "focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40",
            "dark:bg-stone-800 dark:text-stone-100",
            error ? "border-rose-400" : "border-stone-300 dark:border-stone-700",
            icono && "pl-11"
          ),
          ...resto
        }
      )
    ] }),
    (error || ayuda) && /* @__PURE__ */ jsx(
      "p",
      {
        id: `${campoId}-ayuda`,
        className: cx(
          "mt-1 text-xs",
          error ? "text-rose-600 dark:text-rose-300" : "text-stone-500 dark:text-stone-400"
        ),
        children: error ?? ayuda
      }
    )
  ] });
}
function CampoNumero({
  valor,
  onCambiar,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  etiqueta,
  deshabilitado
}) {
  const ajustar = (d) => onCambiar(Math.max(min, Math.min(max, valor + d)));
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "inline-flex items-center overflow-hidden rounded-2xl border border-stone-300 dark:border-stone-600",
      role: "group",
      "aria-label": etiqueta,
      children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": "Restar",
            disabled: deshabilitado || valor <= min,
            onClick: () => ajustar(-1),
            className: "grid h-11 w-11 place-items-center disabled:opacity-30",
            children: /* @__PURE__ */ jsx(Icono, { nombre: "remove" })
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "w-10 text-center text-base font-semibold tabular-nums", "aria-live": "polite", children: valor }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": "Sumar",
            disabled: deshabilitado || valor >= max,
            onClick: () => ajustar(1),
            className: "grid h-11 w-11 place-items-center disabled:opacity-30",
            children: /* @__PURE__ */ jsx(Icono, { nombre: "add" })
          }
        )
      ]
    }
  );
}
function HojaInferior({ abierta, onCerrar, titulo, children }) {
  const ref = useRef(null);
  const tituloId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (abierta && !d.open) d.showModal?.();
    if (!abierta && d.open) d.close?.();
  }, [abierta]);
  return /* @__PURE__ */ jsx(
    "dialog",
    {
      ref,
      onClose: onCerrar,
      onClick: (e) => e.target === ref.current && onCerrar(),
      "aria-labelledby": titulo ? tituloId : void 0,
      className: "m-0 mx-auto mt-auto w-full max-w-lg rounded-t-3xl bg-white p-0 text-stone-900 shadow-2xl open:animar-subir sm:my-auto sm:rounded-3xl dark:bg-stone-900 dark:text-stone-100",
      children: /* @__PURE__ */ jsxs("div", { className: "p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]", children: [
        /* @__PURE__ */ jsxs("div", { className: "mb-4 flex items-center justify-between gap-2", children: [
          /* @__PURE__ */ jsx("h3", { id: tituloId, className: "text-lg font-medium", children: titulo }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: onCerrar,
              "aria-label": "Cerrar",
              className: "grid h-10 w-10 place-items-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800",
              children: /* @__PURE__ */ jsx(Icono, { nombre: "close" })
            }
          )
        ] }),
        children
      ] })
    }
  );
}
function BarraProgreso({ valor, className }) {
  const pct = Math.round(Math.max(0, Math.min(1, valor)) * 100);
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cx("h-1.5 overflow-hidden rounded-full bg-stone-200 dark:bg-stone-800", className),
      role: "progressbar",
      "aria-valuenow": pct,
      "aria-valuemin": 0,
      "aria-valuemax": 100,
      children: /* @__PURE__ */ jsx("div", { className: "h-full rounded-full bg-primary-600 transition-all", style: { width: `${pct}%` } })
    }
  );
}
function Estadistica({
  valor,
  etiqueta,
  className
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cx(
        "rounded-2xl border border-stone-200 bg-white p-2 text-center dark:border-stone-800 dark:bg-stone-900",
        className
      ),
      children: [
        /* @__PURE__ */ jsx("div", { className: "text-lg font-bold leading-tight tabular-nums", children: valor }),
        /* @__PURE__ */ jsx("div", { className: "text-[11px] text-stone-500 dark:text-stone-400", children: etiqueta })
      ]
    }
  );
}
function IndicadorSync({
  estado,
  pendientes = 0,
  onClick
}) {
  const info = {
    ok: { color: "bg-primary-500", texto: "Sincronizado" },
    pendiente: { color: "bg-amber-400", texto: `${pendientes} por enviar` },
    error: { color: "bg-rose-500", texto: "Hay errores" },
    sin_red: { color: "bg-stone-400", texto: `Sin se\xF1al${pendientes ? ` \xB7 ${pendientes}` : ""}` }
  };
  const { color, texto } = info[estado];
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick,
      className: "inline-flex h-8 items-center gap-2 rounded-full bg-white/15 px-3 text-xs font-medium",
      "aria-label": `Estado de sincronizaci\xF3n: ${texto}`,
      children: [
        /* @__PURE__ */ jsx("span", { className: cx("h-2.5 w-2.5 rounded-full", color, estado === "pendiente" && "animate-pulse") }),
        texto
      ]
    }
  );
}

export {
  cx,
  Icono,
  Boton,
  Tarjeta,
  Chip,
  Campo,
  CampoNumero,
  HojaInferior,
  BarraProgreso,
  Estadistica,
  IndicadorSync
};
//# sourceMappingURL=chunk-3Q2RVIFA.js.map